<template>
  <div class="data-display-wrapper">
    <!-- 加载状态 -->
    <Spin v-if="loading" fix>
      <div class="loading-text">
        <Icon type="ios-loading" size="30" class="spin-icon-load"></Icon>
        <div>{{ langMap.dashboard_loading[lang] }}</div>
      </div>
    </Spin>
    
    <!-- 空状态 -->
    <div v-if="!loading && !hasData" class="empty-state">
      <div class="empty-icon-wrapper">
        <div class="hologram-ring"></div>
        <div class="hologram-ring delay-1"></div>
        <div class="hologram-ring delay-2"></div>
        <div class="empty-icon">💻</div>
      </div>
      <div class="empty-text">{{ langMap.empty[lang] }}</div>
      <div class="empty-subtext">{{ emptyMessage }}</div>
    </div>
    
    <!-- 数据表格 -->
    <div v-if="!loading && hasData" class="data-content">
      <div class="table-wrapper">
        <Table
          :columns="columns"
          :data="tableData"
          stripe
          border
          size="small"
          :height="tableHeight"
        >
          <template slot-scope="{ row }" slot="dataTime">
            {{ formatTime(row.dataTime) }}
          </template>
          
          <!-- CPU 概览 -->
          <template slot-scope="{ row }" slot="overview">
            <div class="cpu-overview">
              <div class="overview-item">
                <span class="label">{{ langMap.dashboard_totalUsage[lang] }}:</span>
                <span class="value usage" :class="getUsageClass(row.totalUsage)">{{ row.totalUsage }}%</span>
              </div>
              <div class="overview-item">
                <span class="label">{{ langMap.dashboard_totalCores[lang] }}:</span>
                <span class="value">{{ row.totalCores }}</span>
              </div>
            </div>
          </template>

          <!-- 核心详情 -->
          <template slot-scope="{ row }" slot="coreDetails">
            <div v-if="row.cpuList && row.cpuList.length" class="core-grid">
              <div
                v-for="(core, index) in row.cpuList"
                :key="index"
                class="core-item"
              >
                <span class="core-name">{{ core.name }}</span>
                <div class="core-metrics">
                  <div class="metric">
                    <span class="metric-label">{{ langMap.dashboard_use[lang] }}:</span>
                    <span class="metric-value" :class="getUsageClass(core.coreUsage)">{{ core.coreUsage }}%</span>
                  </div>
                  <div class="metric">
                    <span class="metric-label">{{ langMap.dashboard_temp[lang] }}:</span>
                    <span class="metric-value" :class="getTempClass(core.coreTemp)">{{ core.coreTemp }}°C</span>
                  </div>
                  <div class="metric">
                    <span class="metric-label">{{ langMap.dashboard_freq[lang] }}:</span>
                    <span class="metric-value">{{ core.realFreqMhz }} MHz</span>
                  </div>
                </div>
              </div>
            </div>
            <span v-else>-</span>
          </template>
        </Table>
      </div>
      
      <!-- 自定义分页器 -->
      <div class="pagination-wrapper">
        <div class="cyber-pagination">
          <!-- 总数信息 -->
          <div class="pagination-info">
            <span class="info-label">{{ langMap.dashboard_totalRecords[lang] }}</span>
            <span class="info-value">{{ pagination.total }}</span>
            <span class="info-divider">|</span>
            <span class="info-text">{{ pageRangeText }}</span>
          </div>
          
          <!-- 页码按钮组 -->
          <div class="pagination-controls">
            <!-- 首页 -->
            <button 
              class="page-btn page-first"
              :disabled="pagination.current === 1"
              @click="handlePageChange(1)"
            >
              <span class="btn-icon">《《</span>
            </button>
            
            <!-- 上一页 -->
            <button 
              class="page-btn page-prev"
              :disabled="pagination.current === 1"
              @click="handlePageChange(pagination.current - 1)"
            >
              <span class="btn-icon">‹</span>
            </button>
            
            <!-- 页码按钮 -->
            <div class="page-numbers">
              <button
                v-for="page in visiblePages"
                :key="page"
                :class="['page-number', { active: page === pagination.current, ellipsis: page === '...' }]"
                :disabled="page === '...'"
                @click="page !== '...' && handlePageChange(page)"
              >
                <span class="number-glow" v-if="page === pagination.current"></span>
                {{ page }}
              </button>
            </div>
            
            <!-- 下一页 -->
            <button 
              class="page-btn page-next"
              :disabled="pagination.current === totalPages"
              @click="handlePageChange(pagination.current + 1)"
            >
              <span class="btn-icon">›</span>
            </button>
            
            <!-- 尾页 -->
            <button 
              class="page-btn page-last"
              :disabled="pagination.current === totalPages"
              @click="handlePageChange(totalPages)"
            >
              <span class="btn-icon">》》</span>
            </button>
          </div>
          
          <!-- 每页条数选择 -->
          <div class="page-size-selector">
            <span class="selector-label">{{ langMap.dashboard_perPage[lang] }}</span>
            <div class="size-options">
              <button
                v-for="size in pageSizeOptions"
                :key="size"
                :class="['size-option', { active: pagination.size === size }]"
                @click="handlePageSizeChange(size)"
              >
                {{ size }}
              </button>
            </div>
          </div>
          
          <!-- 快速跳转 -->
          <div class="page-jumper">
            <span class="jumper-label">{{ langMap.dashboard_jumpTo[lang] }}</span>
            <input 
              type="number" 
              class="jumper-input"
              v-model.number="jumpPage"
              @keyup.enter="handleJumpPage"
              :placeholder="String(pagination.current)"
              min="1"
              :max="totalPages"
            />
            <button class="jumper-btn" @click="handleJumpPage">
              <span class="btn-arrow">→</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import langMap from '@/utils/language'
import { mapState } from 'vuex';

export default {
  name: 'CpuHistoryTable',
  props: {
    loading: {
      type: Boolean,
      default: false
    },
    hasData: {
      type: Boolean,
      default: false
    },
    emptyMessage: {
      type: String,
      default: ''
    },
    tableData: {
      type: Array,
      default: () => []
    },
    pagination: {
      type: Object,
      default: () => ({
        total: 0,
        current: 1,
        size: 20,
        pages: 0
      })
    }
  },
  data() {
    return {
      langMap,
      tableHeight: 500,
      pageSizeOptions: [10, 20, 30, 50],
      jumpPage: null,
      windowHeight: window.innerHeight,
      columns: [
        {
          title: langMap.dashboard_dataTime[this.$store.state.app.language],
          slot: 'dataTime',
          width: 150,
          align: 'center'
        },
        {
          title: langMap.dashboard_cpuOverview[this.$store.state.app.language],
          slot: 'overview',
          width: 140,
          align: 'center'
        },
        {
          title: langMap.dashboard_coreDetails[this.$store.state.app.language],
          slot: 'coreDetails',
          minWidth: 600,
          align: 'left'
        }
      ]
    }
  },
  computed: {
    ...mapState({
      lang: state => state.app.language,
    }),
    tableHeight() {
      return Math.max(400, this.windowHeight - 502);
    },
    totalPages() {
      return Math.ceil(this.pagination.total / this.pagination.size) || 1;
    },
    pageRangeText() {
      const start = (this.pagination.current - 1) * this.pagination.size + 1;
      const end = Math.min(this.pagination.current * this.pagination.size, this.pagination.total);
      return `${start}-${end}`;
    },
    visiblePages() {
      const current = this.pagination.current;
      const total = this.totalPages;
      const pages = [];
      
      if (total <= 7) {
        for (let i = 1; i <= total; i++) {
          pages.push(i);
        }
      } else {
        if (current <= 4) {
          for (let i = 1; i <= 5; i++) {
            pages.push(i);
          }
          pages.push('...');
          pages.push(total);
        } else if (current >= total - 3) {
          pages.push(1);
          pages.push('...');
          for (let i = total - 4; i <= total; i++) {
            pages.push(i);
          }
        } else {
          pages.push(1);
          pages.push('...');
          for (let i = current - 1; i <= current + 1; i++) {
            pages.push(i);
          }
          pages.push('...');
          pages.push(total);
        }
      }
      
      return pages;
    }
  },
  mounted() {
    window.addEventListener('resize', this.handleResize);
    this.updateColumnsTitle();
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize);
  },
  watch: {
    lang() {
      this.updateColumnsTitle();
    }
  },
  methods: {
    updateColumnsTitle() {
      this.columns[0].title = this.langMap.dashboard_dataTime[this.lang];
      this.columns[1].title = this.langMap.dashboard_cpuOverview[this.lang];
      this.columns[2].title = this.langMap.dashboard_coreDetails[this.lang];
      this.columns[3].title = this.langMap.dashboard_robotCode[this.lang];
    },
    formatTime(timestamp) {
      if (!timestamp) return '-';
      const date = new Date(timestamp);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const seconds = String(date.getSeconds()).padStart(2, '0');
      return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
    },
    getUsageClass(usage) {
      const u = parseFloat(usage);
      if (u >= 90) return 'high';
      if (u >= 70) return 'medium';
      return 'normal';
    },
    getTempClass(temp) {
      const t = parseFloat(temp);
      if (t >= 80) return 'high';
      if (t >= 60) return 'medium';
      return 'normal';
    },
    handlePageChange(page) {
      this.$emit('page-change', page);
    },
    handlePageSizeChange(size) {
      this.$emit('size-change', size);
    },
    handleJumpPage() {
      if (!this.jumpPage || this.jumpPage < 1 || this.jumpPage > this.totalPages) {
        this.$Message.warning(this.langMap.dashboard_inputValidPageRange[this.lang].replace('{max}', this.totalPages));
        return;
      }
      this.handlePageChange(this.jumpPage);
      this.jumpPage = null;
    },
    handleResize() {
      this.windowHeight = window.innerHeight;
    }
  }
}
</script>

<style lang="less" scoped>
.data-display-wrapper {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  
  .cpu-overview {
    display: flex;
    flex-direction: column;
    gap: 5px;
    
    .overview-item {
      display: flex;
      justify-content: center;
      align-items: center;
      gap: 8px;
      
      .label {
        color: rgba(255, 255, 255, 0.6);
      }
      
      .value {
        font-weight: 600;
        font-family: 'Courier New', monospace;
        color: #fff;
        
        &.usage {
          &.high { color: #ff4d4f; }
          &.medium { color: #faad14; }
          &.normal { color: #52c41a; }
        }
      }
    }
  }
  
  .core-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
    gap: 12px;
    padding: 2px 0;
    
    .core-item {
      display: flex;
      flex-direction: column;
      background: linear-gradient(135deg, rgba(0, 240, 255, 0.08) 0%, rgba(0, 180, 255, 0.03) 100%);
      border: 1px solid rgba(0, 240, 255, 0.25);
      border-radius: 8px;
      padding: 10px;
      transition: all 0.3s ease;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
      
      &:hover {
        background: linear-gradient(135deg, rgba(0, 240, 255, 0.15) 0%, rgba(0, 180, 255, 0.08) 100%);
        border-color: rgba(0, 240, 255, 0.5);
        box-shadow: 0 4px 12px rgba(0, 240, 255, 0.25);
        transform: translateY(-2px);
      }
      
      .core-name {
        color: #00f0ff;
        font-weight: 700;
        font-size: 14px;
        text-align: center;
        padding: 6px 8px;
        background: rgba(0, 240, 255, 0.15);
        border-radius: 6px;
        margin-bottom: 10px;
        letter-spacing: 0.5px;
        text-shadow: 0 0 10px rgba(0, 240, 255, 0.5);
      }
      
      .core-metrics {
        display: flex;
        flex-direction: column;
        gap: 8px;
        
        .metric {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 2px;
          background: rgba(255, 255, 255, 0.03);
          border-radius: 4px;
          
          .metric-label {
            color: rgba(255, 255, 255, 0.7);
            font-size: 11px;
            font-weight: 500;
          }
          
          .metric-value {
            font-family: 'Courier New', monospace;
            font-size: 13px;
            font-weight: 700;
            padding: 2px 8px;
            border-radius: 4px;
            
            &.high { 
              color: #ff4d4f;
              background: rgba(255, 77, 79, 0.15);
            }
            &.medium { 
              color: #faad14;
              background: rgba(250, 173, 20, 0.15);
            }
            &.normal { 
              color: #52c41a;
              background: rgba(82, 196, 26, 0.15);
            }
          }
        }
      }
    }
  }
}
</style>